// frontend/src/samkalp-admin/tabs/PyqBankTab.js
//
// Admin PYQ bank management: upload past-year exam PDFs (async Gemini
// extraction, polled), review/publish extracted questions, trigger
// predictive question generation. Same tab pattern as CurrentAffairsTab.js.
//
// Questions are organized via an Exam Type -> Subject -> Paper -> Year
// cascading picker (backed by GET .../pyq/summary) rather than one flat
// table — with real data spanning multiple papers/years this is the only
// way to find anything. The selected subject+paper is also the target for
// upload metadata and predictive generation, since those are naturally the
// same "which paper am I working on" concept.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Table, Form, Button, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { Sparkles, Trash2, Eye, EyeOff, FileText, Download, RefreshCw } from 'lucide-react';
import api from '../../api/api';
import { getTenantProjects } from '../../api/api';
import { useAlert } from './shared';
import PdfViewerModal from '../../components/Common/PdfViewerModal';

const PAPERS = ['Prelims', 'CSAT', 'GS1', 'GS2', 'GS3', 'GS4', 'Indian Language', 'English Language', 'GS Essay Paper', 'Optional Subjects'];
const ALL = '__all__';

const PyqBankTab = ({ slug }) => {
    const [projects, setProjects] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [summary, setSummary] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [alert, showAlert] = useAlert();
    const [uploading, setUploading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [blueprint, setBlueprint] = useState(null);
    const [blueprintDraft, setBlueprintDraft] = useState(null);
    const [generatingBlueprint, setGeneratingBlueprint] = useState(false);
    const [savingBlueprint, setSavingBlueprint] = useState(false);
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);
    const [pdfModal, setPdfModal] = useState({ show: false, url: null, title: '' });

    // Shared picker state — drives both the view filter and the upload/
    // predictive-generation target.
    const [examType, setExamType] = useState(ALL);
    const [subjectId, setSubjectId] = useState('');
    const [paper, setPaper] = useState('');
    const [year, setYear] = useState(ALL);

    // Upload-specific metadata — year and group have no sensible default
    // from the view filter (a brand-new paper has neither yet), so they're
    // always explicitly entered per upload rather than reused.
    const [uploadYear, setUploadYear] = useState('');
    const [uploadGroup, setUploadGroup] = useState('');

    const loadUploads = useCallback(() => {
        api.get(`/tenant/${slug}/askos/admin/pyq/uploads`).then(res => setUploads(res.data)).catch(() => {});
    }, [slug]);

    const loadSummary = useCallback(() => {
        api.get(`/tenant/${slug}/askos/admin/pyq/summary`).then(res => setSummary(res.data)).catch(() => setSummary([]));
    }, [slug]);

    useEffect(() => {
        getTenantProjects(slug).then(res => setProjects(res.data || [])).catch(() => setProjects([]));
        loadUploads();
        loadSummary();
    }, [slug, loadUploads, loadSummary]);

    useEffect(() => () => clearInterval(pollRef.current), []);

    // Cascading options derived from the summary rows.
    const examTypes = useMemo(() => Array.from(new Set(summary.map(s => s.exam_type).filter(Boolean))).sort(), [summary]);
    const rowsForType = useMemo(() => examType === ALL ? summary : summary.filter(s => s.exam_type === examType), [summary, examType]);
    const rowsForSubject = useMemo(
        () => subjectId ? rowsForType.filter(s => String(s.subject_project_id) === String(subjectId)) : rowsForType,
        [rowsForType, subjectId]
    );
    const paperOptions = useMemo(() => Array.from(new Set(rowsForSubject.map(s => s.paper).filter(Boolean))).sort(), [rowsForSubject]);
    const rowsForPaper = useMemo(() => paper ? rowsForSubject.filter(s => s.paper === paper) : rowsForSubject, [rowsForSubject, paper]);
    const yearOptions = useMemo(() => Array.from(new Set(rowsForPaper.map(s => s.exam_year))).sort((a, b) => (b || 0) - (a || 0)), [rowsForPaper]);

    // Reset narrower selections when a broader one changes.
    useEffect(() => { setSubjectId(''); setPaper(''); setYear(ALL); }, [examType]);
    useEffect(() => { setPaper(''); setYear(ALL); }, [subjectId]);
    useEffect(() => { setYear(ALL); }, [paper]);

    const loadQuestions = useCallback(() => {
        if (!subjectId || !paper) { setQuestions([]); return; }
        const params = { subject_project_id: subjectId, paper };
        if (year !== ALL) params.exam_year = year;
        api.get(`/tenant/${slug}/askos/admin/pyq/questions`, { params }).then(res => setQuestions(res.data)).catch(() => setQuestions([]));
    }, [slug, subjectId, paper, year]);

    useEffect(() => { loadQuestions(); }, [loadQuestions]);

    const loadBlueprint = useCallback(() => {
        if (!subjectId || !paper) { setBlueprint(null); setBlueprintDraft(null); return; }
        api.get(`/tenant/${slug}/askos/admin/pyq/blueprints`, { params: { subject_project_id: subjectId, paper } })
            .then(res => {
                const b = res.data[0] || null;
                setBlueprint(b);
                setBlueprintDraft(b ? JSON.parse(JSON.stringify(b.structure)) : null);
            })
            .catch(() => { setBlueprint(null); setBlueprintDraft(null); });
    }, [slug, subjectId, paper]);

    useEffect(() => { loadBlueprint(); }, [loadBlueprint]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!subjectId) { showAlert('Select a subject before uploading.', 'danger'); e.target.value = ''; return; }
        if (!uploadYear) { showAlert('Enter the exam year before uploading.', 'danger'); e.target.value = ''; return; }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subject_project_id', subjectId);
            formData.append('exam_year', uploadYear);
            formData.append('paper', examType !== ALL ? examType : (paper || 'Prelims'));
            if (uploadGroup.trim()) formData.append('paper_group', uploadGroup.trim());
            const res = await api.post(`/tenant/${slug}/askos/admin/pyq/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            showAlert(`Upload started (#${res.data.upload_id}) — extracting…`);
            setUploadGroup('');
            loadUploads();
            clearInterval(pollRef.current);
            pollRef.current = setInterval(() => { loadUploads(); loadSummary(); loadQuestions(); }, 4000);
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Upload failed.', 'danger');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const generatePredictive = async () => {
        if (!subjectId || !paper) { showAlert('Select a subject and paper first.', 'danger'); return; }
        setGenerating(true);
        try {
            const res = await api.post(`/tenant/${slug}/askos/admin/pyq/generate-predictive`, {
                subject_project_id: Number(subjectId),
                paper,
            });
            const skipped = res.data.skipped_groups?.length ? ` (skipped: ${res.data.skipped_groups.join(', ')} — no historical examples)` : '';
            showAlert(`Generated ${res.data.inserted} predictive questions${skipped}.`);
            loadSummary(); loadQuestions();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Generation failed.', 'danger');
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateBlueprint = async () => {
        const sourceUploadId = questions.find(q => q.source_type === 'historical' && q.source_upload_id)?.source_upload_id;
        if (!sourceUploadId) { showAlert('No extracted historical questions with a source upload found for this selection.', 'danger'); return; }
        setGeneratingBlueprint(true);
        try {
            await api.post(`/tenant/${slug}/askos/admin/pyq/blueprints/generate`, {
                subject_project_id: Number(subjectId), paper, source_upload_id: sourceUploadId,
            });
            showAlert('Blueprint generated — review and approve below.');
            loadBlueprint();
            loadQuestions();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Blueprint generation failed.', 'danger');
        } finally {
            setGeneratingBlueprint(false);
        }
    };

    const updateGroupField = (sectionIdx, groupIdx, field, value) => {
        setBlueprintDraft(draft => {
            const next = JSON.parse(JSON.stringify(draft));
            next.sections[sectionIdx].groups[groupIdx][field] = value;
            return next;
        });
    };

    const handleSaveBlueprint = async (approve) => {
        setSavingBlueprint(true);
        try {
            const payload = { structure: blueprintDraft };
            if (approve) payload.status = 'approved';
            await api.patch(`/tenant/${slug}/askos/admin/pyq/blueprints/${blueprint.id}`, payload);
            showAlert(approve ? 'Blueprint approved.' : 'Blueprint saved.');
            loadBlueprint();
            loadQuestions();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Save failed.', 'danger');
        } finally {
            setSavingBlueprint(false);
        }
    };

    const togglePublish = async (q) => {
        try {
            await api.patch(`/tenant/${slug}/askos/admin/pyq/questions/${q.id}/publish`, { is_published: !q.is_published });
            loadQuestions();
        } catch (err) {
            showAlert('Could not update.', 'danger');
        }
    };

    const deleteQuestion = async (q) => {
        if (!window.confirm('Delete this question?')) return;
        try {
            await api.delete(`/tenant/${slug}/askos/admin/pyq/questions/${q.id}`);
            loadSummary();
            loadQuestions();
        } catch (err) {
            showAlert('Delete failed.', 'danger');
        }
    };

    const bulkPublish = async (is_published) => {
        if (!subjectId || !paper) return;
        try {
            const res = await api.patch(`/tenant/${slug}/askos/admin/pyq/questions/bulk-publish`, {
                subject_project_id: Number(subjectId), paper, is_published,
            });
            showAlert(`${is_published ? 'Published' : 'Unpublished'} ${res.data.updated} question(s).`);
            loadQuestions();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Bulk update failed.', 'danger');
        }
    };

    const openUploadPdf = async (uploadId, download, title) => {
        if (download) {
            try {
                const res = await api.get(`/tenant/${slug}/askos/admin/pyq/uploads/${uploadId}/pdf`, { params: { download } });
                const url = res.data.url;
                const a = document.createElement('a');
                a.href = url; a.download = ''; document.body.appendChild(a); a.click(); a.remove();
            } catch (err) {
                showAlert(err.response?.data?.detail || 'Could not load PDF.', 'danger');
            }
            return;
        }
        setPdfModal({ show: true, url: null, title: title || 'PDF Preview' });
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/pyq/uploads/${uploadId}/pdf`, { params: { download: false } });
            setPdfModal(m => ({ ...m, url: res.data.url }));
        } catch (err) {
            setPdfModal({ show: false, url: null, title: '' });
            showAlert(err.response?.data?.detail || 'Could not load PDF.', 'danger');
        }
    };

    const reextractUpload = async (uploadId) => {
        if (!window.confirm('Re-extract this PDF? Its existing historical questions will be replaced.')) return;
        try {
            await api.post(`/tenant/${slug}/askos/admin/pyq/uploads/${uploadId}/reextract`);
            showAlert('Re-extraction started…');
            loadUploads();
            clearInterval(pollRef.current);
            pollRef.current = setInterval(() => { loadUploads(); loadSummary(); loadQuestions(); }, 4000);
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Re-extraction failed to start.', 'danger');
        }
    };

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small">{alert.msg}</Alert>}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-2">Browse by Exam Type / Subject / Paper / Year</h6>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Button size="sm" variant={examType === ALL ? 'dark' : 'outline-secondary'} onClick={() => setExamType(ALL)}>All Types</Button>
                        {(examTypes.length ? examTypes : PAPERS).map(t => (
                            <Button key={t} size="sm" variant={examType === t ? 'dark' : 'outline-secondary'} onClick={() => setExamType(t)}>{t}</Button>
                        ))}
                    </div>
                    <Row className="g-2">
                        <Col md={4}>
                            <Form.Select size="sm" value={subjectId} onChange={e => setSubjectId(e.target.value)}>
                                <option value="">Select subject…</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Select size="sm" value={paper} onChange={e => setPaper(e.target.value)} disabled={!subjectId}>
                                <option value="">Select paper…</option>
                                {paperOptions.map(p => <option key={p} value={p}>{p}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Select size="sm" value={year} onChange={e => setYear(e.target.value)} disabled={!paper}>
                                <option value={ALL}>All years</option>
                                {yearOptions.map(y => <option key={y ?? 'none'} value={y ?? ''}>{y || 'Unspecified'}</option>)}
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-3">Upload Past Paper (PDF)</h6>
                    <p className="small text-muted mb-2">
                        Subject and Exam Type come from the picker above (currently: {subjectId ? projects.find(p => String(p.id) === String(subjectId))?.name : 'none selected'} / {examType === ALL ? 'none selected' : examType}).
                        Set the exam year and specific paper/group below, then choose the file.
                    </p>
                    <Row className="g-2 mb-2">
                        <Col md={3}>
                            <Form.Control size="sm" type="number" placeholder="Exam year (e.g. 2025)" value={uploadYear} onChange={e => setUploadYear(e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Form.Control size="sm" placeholder="Paper / Group (e.g. PSIR Paper I)" value={uploadGroup} onChange={e => setUploadGroup(e.target.value)} />
                        </Col>
                    </Row>
                    <Form.Text className="text-muted d-block mb-2">
                        Paper/Group names this specific paper (e.g. distinguishing "Paper I" from "Paper II" within an Optional Subject) — every extracted question is tagged with it consistently, instead of each question getting Gemini's own (sometimes inconsistent) guess.
                    </Form.Text>
                    <Form.Control ref={fileInputRef} type="file" accept="application/pdf" size="sm" onChange={handleUpload} disabled={uploading} />
                    {uploading && <div className="small text-muted mt-2"><Spinner animation="border" size="sm" className="me-2" />Uploading…</div>}

                    <Table size="sm" className="mb-0 mt-3">
                        <thead><tr><th>File</th><th>Year</th><th>Group</th><th>Status</th><th>Questions</th><th>Uploaded</th><th className="text-end">PDF</th></tr></thead>
                        <tbody>
                            {uploads.map(u => (
                                <tr key={u.id}>
                                    <td className="x-small">{u.source_filename}</td>
                                    <td className="x-small">{u.exam_year || <span className="text-muted">—</span>}</td>
                                    <td className="x-small">{u.paper_group || <span className="text-muted">—</span>}</td>
                                    <td><Badge bg={u.status === 'extracted' ? 'success' : u.status === 'error' ? 'danger' : 'secondary'}>{u.status}</Badge></td>
                                    <td className="x-small">{u.questions_extracted}</td>
                                    <td className="x-small text-muted">{new Date(u.created_at).toLocaleString()}</td>
                                    <td className="text-end">
                                        <Button variant="link" size="sm" className="p-1" title="Preview PDF" onClick={() => openUploadPdf(u.id, false, `${u.paper_group || u.source_filename}${u.exam_year ? ` — ${u.exam_year}` : ''}`)}>
                                            <FileText size={14} />
                                        </Button>
                                        <Button variant="link" size="sm" className="p-1" title="Download PDF" onClick={() => openUploadPdf(u.id, true)}>
                                            <Download size={14} />
                                        </Button>
                                        <Button variant="link" size="sm" className="p-1" title="Re-extract (replaces this upload's historical questions)" onClick={() => reextractUpload(u.id)}>
                                            <RefreshCw size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {uploads.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-3">No uploads yet.</td></tr>}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-3">QP Blueprint</h6>
                    <p className="small text-muted">The real paper's structure — sections, compulsory vs. choice question groups, marks, word limits. Predictive generation and mock-test building both require this to be approved first, so students get questions that actually match the exam's real shape.</p>
                    {!subjectId || !paper ? (
                        <p className="small text-muted fst-italic">Select a subject and paper above first.</p>
                    ) : !blueprint ? (
                        <Button size="sm" onClick={handleGenerateBlueprint} disabled={generatingBlueprint}>
                            {generatingBlueprint ? <Spinner animation="border" size="sm" /> : <><Sparkles size={14} className="me-1" />Generate Blueprint</>}
                        </Button>
                    ) : (
                        <>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Badge bg={blueprint.status === 'approved' ? 'success' : 'warning'} text={blueprint.status === 'approved' ? undefined : 'dark'}>{blueprint.status}</Badge>
                                <span className="small text-muted">{blueprint.total_marks ?? '—'} total marks · {blueprint.total_generatable_questions} questions defined</span>
                                <Button size="sm" variant="outline-secondary" onClick={handleGenerateBlueprint} disabled={generatingBlueprint}>Re-generate</Button>
                            </div>
                            {blueprintDraft?.sections?.map((section, sIdx) => (
                                <div key={section.name} className="mb-3">
                                    <p className="fw-semibold small mb-1">{section.name}</p>
                                    <Table size="sm" className="mb-0">
                                        <thead><tr><th>Group</th><th>Compulsory</th><th>Attempt</th><th>Of</th><th>Marks each</th><th>Word limit</th></tr></thead>
                                        <tbody>
                                            {section.groups.map((g, gIdx) => (
                                                <tr key={g.group_label}>
                                                    <td className="x-small">{g.group_label}</td>
                                                    <td>
                                                        <Form.Check
                                                            type="switch"
                                                            checked={!!g.compulsory}
                                                            onChange={e => updateGroupField(sIdx, gIdx, 'compulsory', e.target.checked)}
                                                        />
                                                    </td>
                                                    <td><Form.Control size="sm" type="number" style={{ width: 60 }} value={g.attempt_count ?? ''} disabled={g.compulsory} onChange={e => updateGroupField(sIdx, gIdx, 'attempt_count', e.target.value ? Number(e.target.value) : null)} /></td>
                                                    <td><Form.Control size="sm" type="number" style={{ width: 60 }} value={g.of_count ?? ''} disabled={g.compulsory} onChange={e => updateGroupField(sIdx, gIdx, 'of_count', e.target.value ? Number(e.target.value) : null)} /></td>
                                                    <td><Form.Control size="sm" type="number" style={{ width: 70 }} value={g.marks_each ?? ''} onChange={e => updateGroupField(sIdx, gIdx, 'marks_each', e.target.value ? Number(e.target.value) : null)} /></td>
                                                    <td><Form.Control size="sm" type="number" style={{ width: 70 }} value={g.word_limit ?? ''} onChange={e => updateGroupField(sIdx, gIdx, 'word_limit', e.target.value ? Number(e.target.value) : null)} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ))}
                            <div className="d-flex gap-2">
                                <Button size="sm" variant="outline-primary" onClick={() => handleSaveBlueprint(false)} disabled={savingBlueprint}>Save Edits</Button>
                                <Button size="sm" onClick={() => handleSaveBlueprint(true)} disabled={savingBlueprint}>
                                    {blueprint.status === 'approved' ? 'Save & Keep Approved' : 'Save & Approve'}
                                </Button>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-3">Generate Predictive Questions</h6>
                    <p className="small text-muted">Generates new questions matching the approved blueprint's exact structure (per-group counts, marks, word limits) — no arbitrary count.</p>
                    {!subjectId || !paper ? (
                        <p className="small text-muted fst-italic">Select a subject and paper above first.</p>
                    ) : blueprint?.status !== 'approved' ? (
                        <p className="small text-muted fst-italic">Generate and approve a blueprint above first.</p>
                    ) : (
                        <Button size="sm" onClick={generatePredictive} disabled={generating}>
                            {generating ? <Spinner animation="border" size="sm" /> : <><Sparkles size={14} className="me-1" />Generate from Blueprint</>}
                        </Button>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">Questions ({questions.length})</h6>
                        {subjectId && paper && questions.length > 0 && (
                            <div className="d-flex gap-2">
                                <Button size="sm" variant="outline-success" onClick={() => bulkPublish(true)}>Publish All</Button>
                                <Button size="sm" variant="outline-secondary" onClick={() => bulkPublish(false)}>Unpublish All</Button>
                            </div>
                        )}
                    </div>
                    {(!subjectId || !paper) && <p className="text-center text-muted py-3">Select a subject and paper above to view its questions.</p>}
                    {subjectId && paper && (
                        <Table hover responsive size="sm" className="align-middle">
                            <thead className="table-light">
                                <tr><th>Question</th><th>Year</th><th>Group</th><th>Source</th><th>Published</th><th className="text-end">Actions</th></tr>
                            </thead>
                            <tbody>
                                {questions.map(q => (
                                    <tr key={q.id}>
                                        <td className="x-small" style={{ maxWidth: 400 }}>{q.question_text}</td>
                                        <td><Badge bg="light" text="dark" className="border">{q.exam_year || '—'}</Badge></td>
                                        <td>{q.blueprint_group ? <Badge bg="light" text="dark" className="border">{q.blueprint_group}</Badge> : <span className="text-muted x-small">—</span>}</td>
                                        <td><Badge bg={q.source_type === 'predicted' ? 'warning' : 'info'} text={q.source_type === 'predicted' ? 'dark' : undefined}>{q.source_type}</Badge></td>
                                        <td>{q.is_published ? <Badge bg="success">published</Badge> : <Badge bg="secondary">hidden</Badge>}</td>
                                        <td className="text-end">
                                            <Button variant="link" size="sm" className="p-1" title={q.is_published ? 'Unpublish' : 'Publish'} onClick={() => togglePublish(q)}>
                                                {q.is_published ? <EyeOff size={14} /> : <Eye size={14} />}
                                            </Button>
                                            <Button variant="link" size="sm" className="p-1 text-danger" title="Delete" onClick={() => deleteQuestion(q)}>
                                                <Trash2 size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {questions.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">No questions yet for this selection.</td></tr>}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <PdfViewerModal
                show={pdfModal.show}
                url={pdfModal.url}
                title={pdfModal.title}
                onHide={() => setPdfModal({ show: false, url: null, title: '' })}
            />
        </>
    );
};

export default PyqBankTab;
